import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../../utils/api";
import { FaSearch, FaFilter, FaSortAmountDown, FaSortAmountUp, FaPlus, FaEye, FaEdit, FaTrash, FaUserGraduate } from "react-icons/fa";
import "./TeacherStyles.css";

function StudentList() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterBatch, setFilterBatch] = useState("all");
  const [batches, setBatches] = useState([]);
  const [filterType, setFilterType] = useState("batch");
  const [rollNumber, setRollNumber] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchStudents();

    fetchBatches();
  }, [sortOrder, filterBatch]);

  const fetchStudents = async () => {
    console.log("Fetching students...");
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const response = await apiService.getTeachetBatchStudents(user.batch);

      if (response && response.data && response.data.success) {
        setStudents(response.data.data || []);
      } else {
        console.error("API response unsuccessful:", response);
        setError("Failed to load students");
      }
    } catch (err) {
      console.error("Students fetch error:", err);
      setError("Error fetching students: " + (err.response?.data?.message || err.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await apiService.getActiveTeacherBatches();
      if (response && response.data && response.data.success) {
        setBatches(response.data.batches || []);
      }
    } catch (err) {
      console.error("Batches fetch error:", err);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const handleFilterChange = (e) => {
    setFilterBatch(e.target.value);
  };

  const handleFilterTypeChange = (e) => {
    setFilterType(e.target.value);
  };

  const handleRollNumberChange = (e) => {
    setRollNumber(e.target.value);
  };

  const handleFilterSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let response;
      const user = JSON.parse(localStorage.getItem("user"));

      if (filterType === "batch") {
        response = await apiService.getTeachetBatchStudents(filterBatch !== "all" ? filterBatch : user.batch);
      } else {
        // Filter by roll number
        response = await apiService.getStudentByRollNumber(rollNumber);
      }

      if (response && response.data && response.data.success) {
        setStudents(response.data.data || []);
      } else {
        console.error("API response unsuccessful:", response);
        setError("Failed to load students");
      }
    } catch (err) {
      console.error("Students fetch error:", err);
      setError("Error fetching students: " + (err.response?.data?.message || err.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const handleViewStudent = (id) => {
    navigate(`/dashboard/teacher/students/${id}`);
  };

  const handleAddStudent = () => {
    navigate("/students/add");
  };

  const handleEditStudent = (id, e) => {
    e.stopPropagation(); // Prevent triggering the card click
    navigate(`/students/${id}/edit`);
  };

  const handleDeleteStudent = (id, e) => {
    e.stopPropagation(); // Prevent triggering the card click
    // Implement student deletion logic with confirmation
    if (window.confirm("Are you sure you want to remove this student?")) {
      console.log("Deleting student:", id);
      // Call API to delete student
      // Then refresh the student list
      // fetchStudents();
    }
  };

  // Filter students based on search term
  const filteredStudents = students.filter((student) => student.username.toLowerCase().includes(searchTerm.toLowerCase()) || student.email.toLowerCase().includes(searchTerm.toLowerCase()) || (student.rollNo && student.rollNo.toLowerCase().includes(searchTerm.toLowerCase())));

  return (
    <div className="students-container">
      <div className="section-header">
        <h2>My Students</h2>
        <div className="section-actions">
          <div className="search-bar">
            <FaSearch />
            <input type="text" placeholder="Search students..." value={searchTerm} onChange={handleSearch} />
          </div>
        </div>
      </div>

      <form className="filter-form" onSubmit={handleFilterSubmit}>
        <div className="filter-controls">
          <div className="filter-type-selector">
            {/* <label htmlFor="filterType">Filter by:</label> */}
            <select id="filterType" value={filterType} onChange={handleFilterTypeChange} className="filter-type-select">
              <option value="batch">Batch</option>
              <option value="rollNumber">Roll Number</option>
            </select>
          </div>

          {filterType === "batch" ? (
            <div className="filter-group">
              <FaFilter />
              <select value={filterBatch} onChange={handleFilterChange}>
                <option value="all">All Batches</option>
                {batches.map((batch) => (
                  <option key={batch.id} value={batch.id}>
                    {batch.name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="filter-group">
              <FaFilter />
              <input type="text" placeholder="Enter Roll Number" value={rollNumber} onChange={handleRollNumberChange} />
            </div>
          )}

          <button type="submit" className="filter-button">
            Apply Filter
          </button>
        </div>
      </form>

      {loading ? (
        <div className="loading-spinner"></div>
      ) : error ? (
        <p className="error-message">{error}</p>
      ) : filteredStudents.length === 0 ? (
        <div className="empty-message">
          <FaUserGraduate className="empty-icon" />
          <p>No students found. Add students to your batches.</p>
          {/* <button className="action-button add-button" onClick={handleAddStudent}>
            <FaPlus /> Add Student
          </button> */}
        </div>
      ) : (
        <div className="students-grid">
          {filteredStudents.map((student) => (
            <div key={student.id} className="student-card" onClick={() => handleViewStudent(student.id)}>
              <img className="student-avatar" src={student.profilePicture} alt={student.username} />
              <div className="student-info">
                <h4>{student.username}</h4>
                <p className="student-email">{student.email}</p>
                {student.rollNo && <p className="student-roll">Roll No: {student.rollNo}</p>}
                <p className="student-batch">Batch: {student.enrolledBatches.map((batch) => batch.name).join(", ")}</p>
              </div>
              {/* <div className="student-actions">
                <button className="icon-button view-button" onClick={() => handleViewStudent(student.id)}>
                  <FaEye />
                </button>
                <button className="icon-button edit-button" onClick={(e) => handleEditStudent(student.id, e)}>
                  <FaEdit />
                </button>
                <button className="icon-button delete-button" onClick={(e) => handleDeleteStudent(student.id, e)}>
                  <FaTrash />
                </button>
              </div> */}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default StudentList;
